import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Plus, Search, Trash2, Filter } from "lucide-react";
import {
  attachQuestionsToCategory,
  detachQuestionFromCategory,
  fetchCategoryQuestions,
} from "../../redux/slices/categorySlice";
import { fetchQuestions } from "../../redux/slices/questionSlice";
import { pickLocalized } from "../../lib/localized";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useToast } from "../../components/ui/use-toast";
import {
  questionListClass,
  questionListClassTall,
  questionRowHover,
} from "../../lib/quizStyles";

/**
 * Manage many-to-many practice mappings for a category/subcategory.
 * Admins can map any question from the bank (not only home-category matches).
 */
function CategoryQuestionsDialog({ open, onOpenChange, category }) {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const {
    categoryQuestions = [],
    categoryQuestionsLoading,
    categoryQuestionsPagination,
  } = useSelector((s) => s.category);
  const { questions: bankQuestions = [], loading: bankLoading } = useSelector(
    (s) => s.question
  );

  const [mappedSearch, setMappedSearch] = useState("");
  const [bankSearch, setBankSearch] = useState("");
  const [difficulty, setDifficulty] = useState("all");
  const [questionType, setQuestionType] = useState("all");
  const [busyId, setBusyId] = useState(null);

  const categoryId = category?.id;
  const categoryName = pickLocalized(category?.name) || "Category";

  const loadMapped = useCallback(() => {
    if (!categoryId) return;
    dispatch(
      fetchCategoryQuestions({
        categoryId,
        params: {
          search: mappedSearch.trim() || undefined,
          limit: 100,
        },
      })
    );
  }, [dispatch, categoryId, mappedSearch]);

  const loadBank = useCallback(() => {
    dispatch(
      fetchQuestions({
        page: 1,
        limit: 100,
        search: bankSearch.trim() || undefined,
        ...(difficulty !== "all" && { difficulty }),
        ...(questionType !== "all" && { question_type: questionType }),
      })
    );
  }, [dispatch, bankSearch, difficulty, questionType]);

  useEffect(() => {
    if (!open || !categoryId) return;
    setMappedSearch("");
    setBankSearch("");
    setDifficulty("all");
    setQuestionType("all");
    dispatch(
      fetchCategoryQuestions({ categoryId, params: { limit: 100 } })
    );
    dispatch(fetchQuestions({ page: 1, limit: 100 }));
  }, [open, categoryId, dispatch]);

  const mappedIds = useMemo(
    () => new Set(categoryQuestions.map((q) => q.id)),
    [categoryQuestions]
  );

  const bankFiltered = useMemo(() => {
    return bankQuestions.filter((q) => !mappedIds.has(q.id));
  }, [bankQuestions, mappedIds]);

  const handleAttach = async (question) => {
    if (!categoryId || busyId) return;
    setBusyId(question.id);
    try {
      await dispatch(
        attachQuestionsToCategory({
          categoryId,
          questionIds: [question.id],
        })
      ).unwrap();
      toast({
        title: "Mapped",
        description: "Question added to this category’s practice set.",
      });
      loadMapped();
    } catch (err) {
      toast({
        title: "Error",
        description: err || "Failed to map question",
        variant: "destructive",
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleDetach = async (questionId) => {
    if (!categoryId || busyId) return;
    setBusyId(questionId);
    try {
      await dispatch(
        detachQuestionFromCategory({ categoryId, questionId })
      ).unwrap();
      toast({
        title: "Unmapped",
        description: "Question removed from this category (still in the bank).",
      });
      loadMapped();
    } catch (err) {
      toast({
        title: "Error",
        description: err || "Failed to unmap question",
        variant: "destructive",
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Questions — {categoryName}</DialogTitle>
          <DialogDescription>
            Map any question from the bank into this category for practice. The
            same question can belong to multiple categories.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-1">
          {/* Currently mapped */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Label>
                Mapped for practice (
                {categoryQuestionsPagination?.total ?? categoryQuestions.length}
                )
              </Label>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Filter mapped…"
                  value={mappedSearch}
                  onChange={(e) => setMappedSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadMapped()}
                  className="pl-10 h-9"
                />
              </div>
            </div>
            <div className={questionListClass}>
              {categoryQuestionsLoading && categoryQuestions.length === 0 ? (
                <p className="p-4 text-center text-sm text-muted-foreground">
                  Loading…
                </p>
              ) : categoryQuestions.length === 0 ? (
                <p className="p-4 text-center text-sm text-muted-foreground">
                  No questions mapped yet — search the bank below to add some.
                </p>
              ) : (
                <div className="divide-y divide-border">
                  {categoryQuestions.map((q) => (
                    <div
                      key={q.id}
                      className="p-3 flex items-center gap-3 flex-wrap"
                    >
                      <span className="flex-1 text-sm min-w-[140px]">
                        {pickLocalized(q.question_text) || "Untitled"}
                      </span>
                      <Badge variant="secondary" className="text-xs">
                        {q.question_type}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {q.difficulty}
                      </Badge>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={busyId === q.id}
                        onClick={() => handleDetach(q.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Add from bank */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Add from question bank
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="relative sm:col-span-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search bank…"
                  value={bankSearch}
                  onChange={(e) => setBankSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadBank()}
                  className="pl-10 h-9"
                />
              </div>
              <Select value={difficulty} onValueChange={setDifficulty}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Difficulty" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All difficulties</SelectItem>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
              <Select value={questionType} onValueChange={setQuestionType}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="mcq">MCQ</SelectItem>
                  <SelectItem value="multiple_select">Multiple select</SelectItem>
                  <SelectItem value="true_false">True / False</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={loadBank}
                disabled={bankLoading}
              >
                Apply filters
              </Button>
            </div>
            <div className={questionListClassTall}>
              {bankLoading && bankFiltered.length === 0 ? (
                <p className="p-4 text-center text-sm text-muted-foreground">
                  Loading bank…
                </p>
              ) : bankFiltered.length === 0 ? (
                <p className="p-4 text-center text-sm text-muted-foreground">
                  No matching questions left to add. Try different filters or
                  create questions in the Question Bank.
                </p>
              ) : (
                <div className="divide-y divide-border">
                  {bankFiltered.map((question) => (
                    <div
                      key={question.id}
                      className={`p-3 flex items-center gap-3 ${questionRowHover}`}
                    >
                      <span className="flex-1 text-sm min-w-[140px]">
                        {pickLocalized(question.question_text) || "Untitled"}
                      </span>
                      <Badge variant="secondary" className="text-xs">
                        {question.question_type}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {question.difficulty}
                      </Badge>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={busyId === question.id}
                        onClick={() => handleAttach(question)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CategoryQuestionsDialog;
